import React from "react";
import {
    IFormWithErrors,
    useFocusFirstFormError,
} from "tsi-common-react/src/forms/hooks/useFocusFirstFormError";
import { t } from "ttag";

/**
 * Contact / order-issue form enhancements shared by ContactFormPage and
 * OrderIssuePage (both set `body.contact-us-page` and render the `#reasons`
 * recipients select + the `#message` field):
 *
 *  - On `#reasons` change, the message field's label swaps to "Mailing Address:"
 *    for the "Unsubscribe from Postal" reason and "Your Message:" otherwise. The
 *    msgids are fixed so the existing translations keep matching.
 *  - On mount, focus the first errored field's input
 *    (useFocusFirstFormError).
 *
 * The select stays uncontrolled (it is rendered by the generic form-widget
 * renderer); a delegated `onChange` on the `<form>` drives the label state.
 */
export const useContactFormEnhancements = (
    form: IFormWithErrors,
): {
    formRef: React.RefObject<HTMLFormElement | null>;
    messageLabel: string | null;
    onReasonsChange: (event: React.FormEvent) => void;
} => {
    const formRef = React.useRef<HTMLFormElement>(null);
    const [messageLabel, setMessageLabel] = React.useState<string | null>(null);

    useFocusFirstFormError(form);

    const onReasonsChange = (event: React.FormEvent): void => {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement) || target.id !== "reasons") {
            return;
        }
        const selected = target.item(target.selectedIndex);
        const text = selected?.text ?? "";
        setMessageLabel(
            text === t`Unsubscribe from Postal`
                ? t`Mailing Address:`
                : t`Your Message:`,
        );
    };

    return { formRef, messageLabel, onReasonsChange };
};
